/// <amd-module name="TFS/Admin/Groups/Pivots/GroupMembers/Redux/GroupMembersPivotSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { filterXSS } from "xss";
import { IOperationStatus, Result } from "TFS/Admin/Common/Constants/Constants";
import { DataProviders } from "TFS/Admin/Common/Constants/DataProviderConstants";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import {
    addMembersToGroup,
    checkMembershipExistence,
    fetchGroupByDescriptor,
    fetchServicePrincipalByDescriptor,
    fetchUserByDescriptor,
    getTeamMembers,
    listMembership,
    removeMembership
} from "TFS/Admin/Common/DataAccessLayers/GraphDAL";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { convertIIdentityToGraphMember } from "TFS/Admin/Common/Util/TypeConverters";
import { IEntityType, IPermissionPage, loadAADMembersFeatureFlag, MemberOperation, MembershipQuery, SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { IGroupMembersViewData, PermissionsGroup, SelectableGraphMember } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { GroupMembersPivotActions, GroupMembersPivotActionTypes } from "TFS/Admin/Groups/Common/Redux/Actions/GroupMembersPivotActions";
import * as AadDataSource from "TFS/Admin/Groups/Common/Redux/DataSource/AadDataSource";
import { getTeamAdmins, updateTeamProperties } from "TFS/Admin/Groups/Common/Redux/DataSource/AdminTeamsDataSource";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { convertIdentityRefToGraphMember } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { getErrorMessage, IErrorAction } from "TFS/Admin/Groups/Common/Util/ErrorMessageHelper";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getProjectName, getUrlForGraphSubject } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { getGroupMembersList } from "TFS/Admin/Groups/Pivots/GroupMembers/DataSource/GroupMembersPivotDataSource";
import { IOrganizationAadData } from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { ObservableArray } from "VSS/Core/Observable";
import { caseInsensitiveContains, format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { GraphMembership, GraphSubject, GraphSubjectLookup, GraphSubjectLookupKey, GraphTraversalDirection } from "VSS/Platform/Contracts/Graph";
import { IdentityRef, TeamMember } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLocationService } from "VSS/Platform/Location";
import { IdentityType } from "VSSUI/IdentityPicker";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";

/**
 * Root permissions Members saga.
 */
export function* groupMembersSaga(): SagaIterator {
    // Register the initialize permissions handler
    yield effects.takeEvery(GroupMembersPivotActionTypes.InitView, initializeGroupMembersPivot);
    yield effects.takeLatest(GroupMembersPivotActionTypes.SearchGroupMembers, searchGroupMembers);
    yield effects.takeEvery(GroupMembersPivotActionTypes.DeleteDialogConfirm, deleteGroupMember);
    yield effects.takeEvery(GroupMembersPivotActionTypes.SaveGroupMembers, onSaveGroupMembers);
    yield effects.takeEvery(GroupMembersPivotActionTypes.CheckMembershipExistence, onCheckMembershipExistence);
    yield effects.takeEvery(GroupMembersPivotActionTypes.MultiDeleteClick, onMultiDeleteClick);
    yield effects.takeEvery(GroupMembersPivotActionTypes.FetchOrgSettings, fetchOrgSettings);
    yield effects.takeEvery(GroupMembersPivotActionTypes.ChangeMembershipQuery, fetchGroupMembersByQuery);
}

export function* initializeGroupMembersPivot(action: ActionsOfType<GroupMembersPivotActions, GroupMembersPivotActionTypes.InitView>): SagaIterator {
    const { selectedGroup, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupMembersPivot,
        "Initialize members pivot",
        permissionPage == IPermissionPage.Teams ? Feature.Teams : undefined
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        if (!selectedGroup || !selectedGroup.descriptor) {
            yield effects.call(telemetryLogger.captureError, !selectedGroup ? "No selected group found." : "No descriptor found.");

            yield effects.put(
                GroupMembersPivotActions.memberOperationStatus(MemberOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error)
            );
            yield effects.put(GroupMembersPivotActions.membersLoadCompleted());
            return;
        }
        const { descriptor } = selectedGroup;
        if (!selectedGroup.actionPermissions) {
            yield effects.put(GroupsPivotActions.getGroupPermissions(selectedGroup));
        }

        if (isFeatureFlagEnabled(pageContext, loadAADMembersFeatureFlag, false)) {
            const members: IGroupMembersViewData = (yield effects.call(getGroupMembersList, pageContext, { subjectDescriptor: descriptor })) as any;

            if (!members || !members.identities) {
                yield effects.call(telemetryLogger.captureError, "Tried loading members through data provider: No members found.");

                yield effects.put(
                    GroupMembersPivotActions.memberOperationStatus(MemberOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error)
                );
                yield effects.put(GroupMembersPivotActions.membersLoadCompleted());
            } else if (members.identities.length > 0) {
                yield effects.call(_finishGroupMembersFetch, members.identities, selectedGroup, permissionPage, telemetryLogger);
            } else {
                // Zero members found
                yield effects.put(GroupMembersPivotActions.membersLoadCompleted());
            }
        } else {
            const members: GraphMembership[] = (yield effects.call(listMembership, descriptor, telemetryLogger, GraphTraversalDirection.Down)) as any;

            if (!members) {
                yield effects.call(telemetryLogger.captureError, "Tried loading members through graph API: No members found.");

                yield effects.put(
                    GroupMembersPivotActions.memberOperationStatus(MemberOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error)
                );
                yield effects.put(GroupMembersPivotActions.membersLoadCompleted());
            } else if (members.length > 0) {
                const lookupKeys: GraphSubjectLookupKey[] = members.map(membership => ({ descriptor: membership.memberDescriptor }));
                yield effects.call(_lookUpBatchUsers, lookupKeys, selectedGroup, permissionPage, telemetryLogger);
            } else {
                // Zero members found
                yield effects.put(GroupMembersPivotActions.membersLoadCompleted());
            }
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(GroupMembersPivotActions.memberOperationStatus(MemberOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error));
        yield effects.put(GroupMembersPivotActions.membersLoadCompleted());
    }
}

export function* onMultiDeleteClick(action: ActionsOfType<GroupMembersPivotActions, GroupMembersPivotActionTypes.MultiDeleteClick>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupMembersPivot, "Multi delete members");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const { permissionsGroup, members, numberOfAdmins } = action.payload;
    const multiOperationStatuses: IOperationStatus[] = [];

    // If the permissionsGroup is a team, we have to check that the team will have at least one admin
    // before trying to remove members.
    if (numberOfAdmins > 0 && members.filter(x => x.selected && x.selected.value && x.isAdmin).length >= numberOfAdmins) {
        yield effects.put(
            GroupMembersPivotActions.memberOperationStatus(
                MemberOperation.Delete,
                Resources.DeleteMultipleAdminsError,
                MessageCardSeverity.Error,
                [],
                multiOperationStatuses
            )
        );
        return;
    }

    try {
        const groupDescriptor = permissionsGroup.descriptor;
        const successful: SelectableGraphMember[] = [];

        yield effects.all(
            members.map(member =>
                effects.call(function* () {
                    const status = { name: member.displayName, value: Result.Success, message: "" };
                    try {
                        const { descriptor } = member;
                        yield effects.call(removeMembership, descriptor, groupDescriptor, telemetryLogger, GroupsComponent.GroupMembersPivot);
                        // If the removed member was an admin we also have to remove him as an admin of the group.
                        if (member.isAdmin) {
                            yield effects.call(removeAdmin, groupDescriptor, descriptor);
                        }
                        successful.push(member);
                        multiOperationStatuses.push(status);
                    } catch (error) {
                        multiOperationStatuses.push({ ...status, value: Result.Failed, message: error.message });
                        yield effects.call(telemetryLogger.captureError, error.message || error);
                    }
                })
            )
        );

        let severity: MessageCardSeverity = MessageCardSeverity.Info;
        let resultMessage = successful.length > 0 ? format(Resources.MultiDeleteSuccessMessage, successful.length) : "";
        const failed = members.length - successful.length;
        if (failed > 0) {
            resultMessage += (successful.length > 0 ? Resources.And : "") + format(Resources.MultiDeleteFailureMessage, failed);
            severity = MessageCardSeverity.Error;
        }

        yield effects.put(
            GroupMembersPivotActions.memberOperationStatus(MemberOperation.Delete, resultMessage, severity, successful, multiOperationStatuses)
        );
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(
            GroupMembersPivotActions.memberOperationStatus(
                MemberOperation.Delete,
                Resources.MultiDeleteMembersFailed,
                MessageCardSeverity.Error,
                [],
                multiOperationStatuses
            )
        );
    }
}

function* _lookUpBatchUsers(
    lookupKeys: GraphSubjectLookupKey[],
    selectedGroup: PermissionsGroup,
    permissionPage: IPermissionPage,
    telemetryLogger: GroupsTelemetryLogger
): any {
    if (lookupKeys.length === 0) {
        yield effects.put(GroupMembersPivotActions.membersLoadCompleted());
        return;
    }

    const graphMembers = (yield effects.call(_lookUpSubject, { lookupKeys })) as any;
    let graphSubjects: GraphSubject[] = [];

    if (Object.prototype.toString.call(graphMembers).indexOf("Object") > -1) {
        graphSubjects = Object.keys(graphMembers).map(i => graphMembers[i] as GraphSubject);
    }

    yield effects.call(_finishGroupMembersFetch, graphSubjects, selectedGroup, permissionPage, telemetryLogger);
}

function* _finishGroupMembersFetch(
    graphMembers: (SelectableGraphMember | GraphSubject)[],
    selectedGroup: PermissionsGroup,
    permissionPage: IPermissionPage,
    telemetryLogger: GroupsTelemetryLogger
) {
    const isTeamsPage = permissionPage == IPermissionPage.Teams;
    const pageContext: IVssPageContext = yield effects.getContext("pageContext");
    const locationService: IVssLocationService = yield effects.call([pageContext, pageContext.getService], "IVssLocationService");
    const projectName: string | undefined = permissionPage == IPermissionPage.Project ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

    const users: SelectableGraphMember[] = yield effects.all(
        graphMembers.map(member =>
            effects.call(function* () {
                return {
                    ...member,
                    linkProps: !isTeamsPage && member.descriptor
                        ? {
                            href: yield effects.call(
                                getUrlForGraphSubject,
                                member.descriptor,
                                permissionPage,
                                locationService,
                                telemetryLogger,
                                projectName)
                        } : undefined
                }
            })
        )
    );

    if (isTeamsPage || selectedGroup.subjectKind !== IdentityType.Group) {
        const admins: IdentityRef[] = yield effects.call(getTeamAdmins, pageContext, selectedGroup.descriptor);
        users.forEach(user => {
            user.isAdmin = admins.findIndex(a => a.descriptor == user.descriptor) > -1;
        });
    }

    yield effects.put(users.length > 0
        ? GroupMembersPivotActions.initialUserFetch(new ObservableArray<SelectableGraphMember>(users))
        : GroupMembersPivotActions.membersLoadCompleted()
    );
}

function* _lookUpSubject(lookupKeys: GraphSubjectLookup): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupMembersPivot, "Look up subject");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        return yield effects.call([graphClient, graphClient.lookupSubjects], lookupKeys);
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(GroupMembersPivotActions.memberOperationStatus(MemberOperation.Fetch, Resources.FetchMembersFailed, MessageCardSeverity.Error));
    }
}

export function* searchGroupMembers(action: ActionsOfType<GroupMembersPivotActions, GroupMembersPivotActionTypes.SearchGroupMembers>): SagaIterator {
    const { members, permissionPage, newKeyword, oldKeyword } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupMembersPivot,
        "Search Members",
        permissionPage == IPermissionPage.Teams ? Feature.Teams : undefined
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    if (!newKeyword) {
        // Reset view
        yield effects.put(GroupMembersPivotActions.updateSearchStatus(SearchStatus.None));
    } else if (oldKeyword !== undefined && localeIgnoreCaseComparer(newKeyword, oldKeyword) !== 0) {
        try {
            const projectName =
                permissionPage == IPermissionPage.Project ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

            // Search
            const resultMembers: SelectableGraphMember[] = [];
            for (const member of members.value) {
                if (caseInsensitiveContains(member.displayName, newKeyword)) {
                    let linkprops;
                    if (permissionPage != IPermissionPage.Teams && member.descriptor) {
                        const url = (yield effects.call(
                            getUrlForGraphSubject,
                            member.descriptor,
                            permissionPage,
                            locationService,
                            telemetryLogger,
                            projectName
                        )) as any;
                        linkprops = { href: url };
                    }
                    resultMembers.push({ ...(member as SelectableGraphMember), linkProps: linkprops });
                }
            }
            yield effects.put(GroupMembersPivotActions.updateSearchResult(newKeyword, resultMembers, false));
        } catch (error) {
            yield effects.put(GroupMembersPivotActions.updateSearchStatus(SearchStatus.None)); // Reset view

            yield effects.call(telemetryLogger.captureError, error.message || error);
        }
    } else {
        // Search did not change
        yield effects.put(GroupMembersPivotActions.updateSearchStatus(SearchStatus.Completed));
    }
}

export function* deleteGroupMember(action: ActionsOfType<GroupMembersPivotActions, GroupMembersPivotActionTypes.DeleteDialogConfirm>): SagaIterator {
    const { permissionsGroup, member, numberOfAdmins } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupMembersPivot, "Delete member");
    yield effects.call(telemetryLogger.captureAttemptStart);

    // If the permissionsGroup is a team, we have to check that the team will have at least one admin
    // before trying to remove a member.
    if (member.isAdmin && numberOfAdmins <= 1) {
        yield effects.put(
            GroupMembersPivotActions.memberOperationStatus(MemberOperation.Delete, Resources.DeleteAdminsError, MessageCardSeverity.Error, [member])
        );
        return;
    }

    try {
        const group = (yield effects.call(
            removeMembership,
            member.descriptor,
            permissionsGroup.descriptor,
            telemetryLogger,
            GroupsComponent.GroupMembersPivot
        )) as any;

        if (member.isAdmin) {
            yield effects.call(removeAdmin, permissionsGroup.descriptor, member.descriptor);
        }

        yield effects.put(
            GroupMembersPivotActions.memberOperationStatus(
                MemberOperation.Delete,
                format(Resources.MemberDeleteSuccess, filterXSS(member.displayName)),
                MessageCardSeverity.Info,
                [member]
            )
        );
        return group;
    } catch (error) {
        yield effects.put(
            GroupMembersPivotActions.memberOperationStatus(
                MemberOperation.Delete,
                getErrorMessage(GroupsComponent.GroupMembersPivot, error, IErrorAction.Delete),
                MessageCardSeverity.Error,
                [member]
            )
        );

        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* onSaveGroupMembers(action: ActionsOfType<GroupMembersPivotActions, GroupMembersPivotActionTypes.SaveGroupMembers>): SagaIterator {
    const { subjectIdentities, selectedGroup, scopeDescriptor, isExpandedQuery, permissionPage, supportServicePrincipals } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupMembersPivot,
        "Save member",
        permissionPage == IPermissionPage.Teams ? Feature.Teams : undefined
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const result = (yield effects.call(addMembersToGroup, subjectIdentities, selectedGroup, supportServicePrincipals, telemetryLogger, scopeDescriptor)) as any;
        const projectName = permissionPage == IPermissionPage.Project ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

        const multiOperationStatuses: IOperationStatus[] = result.success
            .map((x: any) => ({
                name: x.subjectIdentity.mail ? x.subjectIdentity.mail : x.subjectIdentity.displayName,
                value: Result.Success,
                message: ""
            }))
            .concat(
                result.failed.map((x: any, index: number) => ({
                    name: x.subjectIdentity.mail ? x.subjectIdentity.mail : x.subjectIdentity.displayName,
                    value: Result.Failed,
                    message: getErrorMessage(GroupsComponent.GroupMembersPivot, result.errors[index], IErrorAction.Add)
                }))
            );

        const members = (yield effects.all(
            result.success.map((member: any) =>
                effects.call(function* () {
                    try {
                        let fetchMemberFunction : (descriptor : string, telemetryLogger: AdminFeatureTelemetryLogger) => any;
                        if (localeIgnoreCaseComparer(member.subjectIdentity.entityType, IdentityType.Group) === 0) {
                            fetchMemberFunction = fetchGroupByDescriptor;
                        } else if (supportServicePrincipals && localeIgnoreCaseComparer(member.subjectIdentity.entityType, IEntityType.ServicePrincipal) === 0){
                            fetchMemberFunction = fetchServicePrincipalByDescriptor;
                        } else {
                            fetchMemberFunction = fetchUserByDescriptor;
                        }
                        
                        const addedMember = yield (effects.call(fetchMemberFunction, member.memberDescriptor, telemetryLogger, GroupsComponent.GroupMembersPivot)) as any;

                        let linkprops;
                        if (permissionPage != IPermissionPage.Teams && addedMember.descriptor) {
                            const url = (yield effects.call(
                                getUrlForGraphSubject,
                                addedMember.descriptor,
                                permissionPage,
                                locationService,
                                telemetryLogger,
                                projectName
                            )) as any;
                            linkprops = { href: url };
                        }

                        return { ...(addedMember as SelectableGraphMember), linkProps: linkprops };
                    } catch (error) {
                        yield effects.call(telemetryLogger.captureError, error.message || error);

                        return convertIIdentityToGraphMember({
                            ...member.subjectIdentity,
                            entityType: member.subjectIdentity.entityType.toLocaleLowerCase()
                        });
                    }
                })
            )
        )) as any;

        let resultMessage: string;
        let cardSeverity: MessageCardSeverity;

        // No success elements; all failed
        if (result.success.length === 0) {
            resultMessage = getErrorMessage(
                GroupsComponent.GroupMembersPivot,
                result.errors && result.errors.length > 0 ? result.errors[0] : null,
                IErrorAction.Add
            );
            cardSeverity = MessageCardSeverity.Error;
        } else if (result.failed.length > 0) {
            // Some success elements however some failed elements aswell
            resultMessage = format(Resources.AddMembersPartialSuccess, result.success.length, result.failed.length);
            cardSeverity = MessageCardSeverity.Warning;
        } else {
            resultMessage = format(
                Resources.AddMembersSuccess,
                result.success.length,
                (permissionPage == IPermissionPage.Teams ? Resources.Team : Resources.Group).toLocaleLowerCase()
            );
            cardSeverity = MessageCardSeverity.Info;
        }

        yield effects.put(
            GroupMembersPivotActions.memberOperationStatus(MemberOperation.Add, resultMessage, cardSeverity, members, multiOperationStatuses)
        );
        if (result.success.length > 0 && isExpandedQuery) {
            yield effects.put(GroupMembersPivotActions.changeMembershipQuery(selectedGroup, MembershipQuery.Expanded, {}));
        }
    } catch (error) {
        yield effects.put(
            GroupMembersPivotActions.memberOperationStatus(
                MemberOperation.Add,
                getErrorMessage(GroupsComponent.GroupMembersPivot, error, IErrorAction.Add),
                MessageCardSeverity.Error,
                [],
                []
            )
        );
        yield effects.call(telemetryLogger.captureError, error.message || error);
    } finally {
        yield effects.put(GroupMembersPivotActions.closeAddMemberPanel());
    }
}

export function* onCheckMembershipExistence(
    action: ActionsOfType<GroupMembersPivotActions, GroupMembersPivotActionTypes.CheckMembershipExistence>
): SagaIterator {
    const { member, groupDescriptor } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupMembersPivot, "Check membership");
    yield effects.call(telemetryLogger.captureAttemptStart);
    try {
        if (member.subjectDescriptor) {
            const membershipExistence = (yield effects.call(
                checkMembershipExistence,
                member.subjectDescriptor,
                groupDescriptor,
                telemetryLogger
            )) as any;
            // If the membership already exist, we add the member to the invalid members list
            if (membershipExistence) {
                yield effects.put(GroupMembersPivotActions.addInvalidMember(member));
            } else {
                yield effects.put(GroupMembersPivotActions.addValidMember(member));
            }
        } else {
            // Let user select the subject in this case
            yield effects.put(GroupMembersPivotActions.addValidMember(member));

            yield effects.call(telemetryLogger.captureTelemetry, { originDirectory: member.originDirectory, originId: member.originId });
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* fetchOrgSettings(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupMembersPivot,
        "Fetch if the Org is AAD Backed"
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const settingsData: IOrganizationAadData = (yield effects.call(AadDataSource.isAadOrg, pageContext)) as any;
        yield effects.put(GroupMembersPivotActions.fetchOrgSettingsCompleted(settingsData));
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* fetchGroupMembersByQuery(action: ActionsOfType<GroupMembersPivotActions, GroupMembersPivotActionTypes.ChangeMembershipQuery>): SagaIterator {
    const { selectedGroup, membershipQuery, queryMembers } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupMembersPivot,
        "Fetch Direct or Extended members"
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    const loaded = queryMembers[membershipQuery];
    if (loaded) {
        yield effects.put(GroupMembersPivotActions.changeMembershipQueryCompleted(membershipQuery, loaded));
        return;
    }

    // Direct members should be loaded at init we only fetch Expanded ones if not loaded
    try {
        const teamMembers: TeamMember[] = (yield effects.call(getTeamMembers, selectedGroup.originId)) as any;

        yield effects.put(
            GroupMembersPivotActions.changeMembershipQueryCompleted(
                membershipQuery,
                new ObservableArray<SelectableGraphMember>(teamMembers.map((x: any) => convertIdentityRefToGraphMember(x)))
            )
        );
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

function* removeAdmin(teamDescriptor: string, adminDescriptor: string): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupMembersPivot,
        "Fetch if the Org is AAD Backed",
        Feature.Teams
    );

    const successCode = 204;

    const removeTeamAdminsResponse: any = (yield effects.call(updateTeamProperties, pageContext, {
        removeAdmins: true,
        teamDescriptor,
        adminDescriptor
    })) as any;

    if (!removeTeamAdminsResponse || removeTeamAdminsResponse.statusCode !== successCode) {
        yield effects.call(
            telemetryLogger.captureDataProviderCall,
            DataProviders.RemoveTeamAdmin,
            false,
            "RemoveTeamAdmin: Data provider did not return success (204) response"
        );
    }
}
